import * as React from 'react'
import {
  CaretSortIcon,
  CheckIcon,
  CommitIcon,
  Cross2Icon,
} from '@radix-ui/react-icons'
import * as Popover from '@radix-ui/react-popover'
import { clsx } from 'clsx'

import type { Commit, Branch } from '@basehub/data/replicache/index'

import { isCommit, slugifyBranchName } from '@basehub/data/replicache/index'
import { useDashboardSlugs } from '../../../hooks/use-dashboard-slugs'
import { Input } from '../input'
import { Button, ButtonLink } from '../button'
import { BranchIcon } from '~/components/icons/branch-icon'
import { useRouter } from 'next/navigation'
import { useTabsStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { ScrollArea } from '../scroll-area'
import { IconButton } from '../icon-button'
import { getPlaygroundDisplayName } from '@basehub/data/replicache/playground/utils'

export const BranchSwitcher = ({
  branches,
  activeBranchOrCommit,
  onBranchCreate,
  disableOnCreate,
  trigger,
  header,
  hideBranchManager,
  inputPlaceholder = 'Find or create a branch...',
  onSelect,
  popoverProps = {
    side: 'bottom',
    align: 'start',
  },
}: {
  branches: Branch[]
  activeBranchOrCommit: Branch | Commit
  onBranchCreate?: (params: { name: string }) => Promise<void>
  disableOnCreate?: boolean
  trigger?: React.ReactNode
  header: { title: string } | false
  hideBranchManager?: boolean
  inputPlaceholder?: string
  onSelect?: (branch: Branch) => void
  popoverProps?: Pick<
    React.ComponentProps<typeof Popover.Content>,
    'side' | 'align' | 'onCloseAutoFocus'
  >
}) => {
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const [isOpen, setIsOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const router = useRouter()
  const addTab = useTabsStore((s) => s.addTab)
  const listItemsRef = React.useRef<HTMLDivElement>(null)
  const [pointerIdle, setPointerIdle] = React.useState(true)
  const { align, onCloseAutoFocus, side } = popoverProps

  const branchNameInput = slugifyBranchName(search)
  const sortedBranches = React.useMemo(() => {
    return [...branches].sort((a, b) => {
      if (a.isDefault) return -1
      if (b.isDefault) return 1
      return a.name.localeCompare(b.name)
    })
  }, [branches])

  const filteredBranches = sortedBranches.filter((branch) => {
    return branch.name.includes(branchNameInput)
  })

  const selectedBranch = filteredBranches[selectedIndex]

  React.useEffect(() => {
    if (!isOpen) {
      setSearch('')
      setSelectedIndex(0)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen) return
    setSelectedIndex(0)
  }, [isOpen, search])

  React.useEffect(() => {
    if (!isOpen || !pointerIdle) return
    const el = listItemsRef.current?.querySelector(
      `[data-item-index="${selectedIndex}"]`
    )
    if (el) {
      el.scrollIntoView({ behavior: 'instant', block: 'nearest' })
    }
  }, [isOpen, selectedIndex, pointerIdle])

  if (!activeBranchOrCommit) {
    throw new Error('No branch found on branch switcher')
  }
  let currentItemIndex = 0
  const hasBranchExactMatch = branches.some(
    (branch) => branch.name.toLowerCase() === search.toLowerCase()
  )

  return (
    <Popover.Root onOpenChange={setIsOpen} open={isOpen}>
      {trigger ?? (
        <Popover.Trigger asChild>
          <Button
            unstyled
            className="flex max-w-[200px] cursor-default items-center justify-between font-medium space-x-1 overflow-hidden text-ellipsis whitespace-nowrap rounded-md border border-dark-border-solid bg-dark-base dark:bg-dark-shade p-1 text-xs leading-4 focus:outline outline-offset-2 outline-dark-control-base hover:bg-dark-shade dark:hover:bg-dark-shade-hover focus-visible:outline disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Switch Branch"
          >
            <span>
              {isCommit(activeBranchOrCommit) ? <CommitIcon /> : <BranchIcon />}
            </span>
            <span className="truncate">
              {isCommit(activeBranchOrCommit)
                ? activeBranchOrCommit.message
                : getPlaygroundDisplayName(activeBranchOrCommit)}
            </span>
            <span>
              <CaretSortIcon className="h-4 w-4 text-dark-muted" />
            </span>
          </Button>
        </Popover.Trigger>
      )}
      <Popover.Portal>
        <Popover.Content
          onCloseAutoFocus={onCloseAutoFocus}
          className="tex-sm shadow-modal z-[200] w-[272px] rounded-md border border-dark-border-solid bg-dark-base"
          sideOffset={5}
          align={align}
          side={side}
        >
          {header && (
            <div className="flex items-center justify-between border-b border-dark-border-solid p-2 pl-2.5">
              <p className="text-xs font-medium">{header.title}</p>
              <Popover.Close asChild>
                <IconButton className="" aria-label="Close">
                  <Cross2Icon />
                </IconButton>
              </Popover.Close>
            </div>
          )}
          <div className="border-b border-dark-border-solid p-2">
            <Input
              size="sm"
              placeholder={inputPlaceholder}
              onChange={(e) => setSearch(e.target.value)}
              value={search}
              autoFocus
              onKeyDown={(e) => {
                setPointerIdle(true)
                switch (e.key) {
                  case 'Escape':
                    setIsOpen(false)
                    e.stopPropagation()
                    break
                  case 'Enter':
                    if (selectedBranch) {
                      if (onSelect && !e.metaKey) {
                        e.preventDefault()
                        onSelect(selectedBranch)
                      } else {
                        const href = `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
                          selectedBranch.name
                        )}`
                        if (e.metaKey) {
                          window.open(href, '_blank')
                        } else {
                          router.push(
                            `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
                              selectedBranch.name
                            )}`
                          )
                        }
                      }
                      setIsOpen(false)
                    } else {
                      const createButtonIsActive =
                        document
                          .querySelector('[data-branch-create-button]')
                          ?.getAttribute('data-item-active') === 'true'
                      if (branchNameInput && createButtonIsActive) {
                        if (disableOnCreate) return
                        onBranchCreate?.({ name: branchNameInput }).then(() => {
                          setIsOpen(false)
                        })
                      } else if (selectedIndex === currentItemIndex - 1) {
                        addTab({
                          key: 'branches:active',
                          partiallyOpen: false,
                        })
                        setIsOpen(false)
                      }
                    }
                    break
                  case 'ArrowDown':
                    e.preventDefault()
                    if (e.metaKey) {
                      setSelectedIndex(currentItemIndex - 1)
                    } else {
                      setSelectedIndex((i) => (i + 1) % currentItemIndex)
                    }
                    break
                  case 'ArrowUp':
                    e.preventDefault()
                    if (e.metaKey) {
                      setSelectedIndex(0)
                    } else {
                      setSelectedIndex(
                        (i) => (i + currentItemIndex - 1) % currentItemIndex
                      )
                    }
                    break
                  case 'Tab':
                    e.preventDefault()
                    setIsOpen(false)
                    break
                  default:
                    break
                }
              }}
            />
          </div>
          <ScrollArea
            viewportClassName="max-h-[min(40vh,500px)]"
            onMouseMove={() => setPointerIdle(false)}
          >
            <div className="p-1.5" ref={listItemsRef}>
              {filteredBranches.map((branch) => {
                const isActive = isCommit(activeBranchOrCommit)
                  ? false
                  : branch.id === activeBranchOrCommit.id

                const _currentItemIndex = currentItemIndex++
                const isSelected = _currentItemIndex === selectedIndex

                return (
                  <ButtonLink
                    unstyled
                    key={branch.id}
                    href={{
                      pathname: `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
                        branch.name
                      )}`,
                    }}
                    className={clsx(
                      'flex items-center justify-between overflow-hidden h-8 p-1.5 text-xs cursor-default rounded-lg scroll-py-5 scroll-my-1',
                      isSelected && 'bg-dark-shade-hover'
                    )}
                    onPointerOver={() => {
                      if (pointerIdle) return
                      setSelectedIndex(_currentItemIndex)
                    }}
                    onClick={(e) => {
                      if (onSelect && !e.metaKey) {
                        e.preventDefault()
                        onSelect(branch)
                      }
                      setIsOpen(false)
                    }}
                  >
                    <span className="flex items-center space-x-2 overflow-hidden">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                        {isActive && <CheckIcon />}
                      </span>
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {branch.name}
                      </span>
                    </span>
                    {branch.isDefault && (
                      <span className="rounded-full border border-dark-border-solid bg-dark-shade px-1.5 leading-snug text-dark-muted font-medium">
                        default
                      </span>
                    )}
                  </ButtonLink>
                )
              })}
              {!!search && (
                <>
                  {!onBranchCreate ? (
                    <span className="p-1.5 rounded-lg min-h-8 items-start text-xs text-dark-muted flex gap-2 text-left justify-start w-full cursor-default">
                      No results
                    </span>
                  ) : hasBranchExactMatch ? null : (
                    <WithItemCount counter={currentItemIndex++}>
                      {(updatedCounter) => {
                        return (
                          <Button
                            unstyled
                            focusStyled
                            data-item-active={updatedCounter === selectedIndex}
                            data-branch-create-button
                            disabled={disableOnCreate}
                            onClick={() => {
                              onBranchCreate({ name: branchNameInput })
                              setIsOpen(false)
                            }}
                            onPointerOver={() => {
                              if (pointerIdle) return
                              setSelectedIndex(updatedCounter)
                            }}
                            className='p-1.5 rounded-lg min-h-8 items-start text-xs text-dark-muted flex gap-2 text-left justify-start w-full cursor-default data-[item-active="true"]:bg-dark-shade-hover'
                            style={{ wordSpacing: 'break-word' }}
                          >
                            <BranchIcon className="mt-0.5 shrink-0" />
                            <span className="break-all">
                              Create branch{' '}
                              <span className="font-mono font-semibold text-dark-bright">
                                {branchNameInput}
                              </span>{' '}
                              from{' '}
                              <span className="font-mono font-semibold text-dark-bright">
                                {isCommit(activeBranchOrCommit)
                                  ? activeBranchOrCommit.id
                                  : activeBranchOrCommit.name}
                              </span>
                            </span>
                          </Button>
                        )
                      }}
                    </WithItemCount>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
          {!hideBranchManager && (
            <WithItemCount counter={currentItemIndex++}>
              {(updatedCounter) => {
                return (
                  <div className="pb-1.5 px-1.5">
                    <hr className="border-dark-border-solid mb-1" />

                    <Button
                      unstyled
                      focusStyled
                      data-item-active={updatedCounter === selectedIndex}
                      onPointerOver={() => {
                        if (pointerIdle) return
                        setSelectedIndex(updatedCounter)
                      }}
                      onClick={() => {
                        addTab({
                          key: 'branches:active',
                          partiallyOpen: false,
                        })
                        setIsOpen(false)
                      }}
                      className='p-1.5 rounded-lg min-h-8 items-center text-xs text-dark-bright flex gap-2 text-left justify-start w-full cursor-default data-[item-active="true"]:bg-dark-shade-hover'
                      style={{ wordSpacing: 'break-word' }}
                    >
                      View all branches
                    </Button>
                  </div>
                )
              }}
            </WithItemCount>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

const WithItemCount = ({
  counter,
  children,
}: {
  counter: number
  children: (updatedCounter: number) => React.ReactNode
}) => {
  return <>{children(counter)}</>
}
